import { Injectable } from '@angular/core';
import { AlertInput } from '@ionic/angular';
import { Guid } from '@models/guid';
import { MediaType } from '@models/imported/SagaBase';
import { DataType } from '@models/imported/SagaBase/Interfaces';
import { TranslatedAlertService } from '@services/translated-alert/translated-alert.service';
import { MediaSource } from '../../schema/enums/media-source';
import { ICustomFile } from '../../schema/interfaces/ICustomFile';
import { CameraProviderService } from '../camera-provider/camera-provider.service';
import { FileChooserService } from '../file-chooser/file-chooser.service';
import { ThumbnailService } from '../thumbnail/thumbnail.service';
import { UploadService } from '../upload/upload.service';

interface MediaSourceLabel {
  mediaSource: MediaSource;
  label: string;
}

const MediaSourceLabels: MediaSourceLabel[] = [
  { mediaSource: MediaSource.Camera, label: 'Mobile.AddPhoto' },
  { mediaSource: MediaSource.Video, label: 'Mobile.AddVideo' },
  { mediaSource: MediaSource.Gallery, label: 'Mobile.AddImage' },
  { mediaSource: MediaSource.File, label: 'Mobile.AddFile' }
];

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  private attachmentMediaSources: MediaSource[] = [MediaSource.Camera, MediaSource.Video, MediaSource.Gallery, MediaSource.File];

  constructor(
    private cameraProvider: CameraProviderService,
    private fileChooser: FileChooserService,
    private thumbnailService: ThumbnailService,
    private uploadService: UploadService,
    private translatedAlert: TranslatedAlertService
  ) {}

  setAttachmentMediaSources(mediaSources: MediaSource[]) {
    this.attachmentMediaSources = mediaSources;
  }

  createAttachment(parentId: Guid): Promise<boolean> {
    return this.selectMediaSource(this.attachmentMediaSources)
      .then(mediaSource => {
        return this.selectFile(mediaSource, parentId)
          .then(customFile => this.setAttachmentDataType(customFile))
          .then(file => this.uploadService.upload(file));
      })
      .catch(console.log.bind(console));
  }

  private selectMediaSource(mediaSources: MediaSource[]): Promise<MediaSource> {
    const radioInputs = this.createRadioInputs(mediaSources);
    radioInputs[0].checked = true;

    return new Promise<MediaSource>((resolve, reject) =>
      this.translatedAlert.show({
        header: 'Mobile.AddAttachment',
        inputs: radioInputs,
        buttons: [
          { text: 'Mobile.Cancel', value: false, handler: () => reject('user canceled') },
          {
            text: 'Mobile.Confirm',
            value: true,
            handler: (mediaSourceLabel: MediaSourceLabel) => resolve(mediaSourceLabel.mediaSource)
          }
        ]
      })
    );
  }

  private createRadioInputs(mediaSources: MediaSource[]): AlertInput[] {
    const mediaSourceLabels: MediaSourceLabel[] = MediaSourceLabels.filter(mediaSourceLabel =>
      mediaSources.includes(mediaSourceLabel.mediaSource)
    );

    return mediaSourceLabels.map(mediaSourceLabel => ({
      type: 'radio',
      label: mediaSourceLabel.label,
      value: mediaSourceLabel
    }));
  }

  createMediaMessage(parentId: Guid, mediaSource: MediaSource): Promise<boolean> {
    return this.selectFile(mediaSource, parentId)
      .then(file => this.uploadService.upload(file, true))
      .catch(console.log.bind(console));
  }

  private selectFile(mediaSource: MediaSource, parentId): Promise<ICustomFile> {
    return (mediaSource === MediaSource.File ? this.fileChooser.choose(mediaSource) : this.cameraProvider.showCamera(mediaSource))
      .then(customFile => (customFile ? customFile : Promise.reject('select file canceled')))
      .then(customFile => this.setParent(customFile, parentId))
      .then(customFile => this.setThumbnail(customFile));
  }

  private setParent(customFile: ICustomFile, parentId: Guid): ICustomFile {
    customFile.parent = parentId;
    return customFile;
  }

  private setThumbnail(customFile: ICustomFile): Promise<ICustomFile> {
    return this.thumbnailService.createThumbnail(customFile).then(thumbnail => {
      customFile.thumbnail = thumbnail;
      return customFile;
    });
  }

  private setAttachmentDataType(customFile: ICustomFile): ICustomFile {
    switch (customFile.dataType) {
      case MediaType.Image:
        customFile.dataType = DataType.Picture;
        break;

      case MediaType.Video:
        customFile.dataType = DataType.Video;
        break;

      case MediaType.Document:
        customFile.dataType = DataType.File;
        break;
    }

    return customFile;
  }
}
